import { useMemo } from 'react';

import { selectCoveringContracts } from '~/components/invoices/detailPage/related/coveringContracts';
import { useParentTickets } from '~/components/invoices/detailPage/useParentTickets';
import { useContractsForProperty } from '~/data/hooks/useContracts';
import useInvoices from '~/data/hooks/useInvoices';
import { useTickets } from '~/data/hooks/useTickets';
import type { Invoice, InvoiceKindKey } from '~/data/schemas/invoice';
import { InvoiceKind } from '~/data/schemas/invoice';
import type { Ticket } from '~/data/schemas/ticket';

export interface RelatedCounts {
  carsServiced: number;
  linkedTickets: number | undefined;
  servicedTickets: number | undefined;
  linkedInvoices: number;
  /** Every other invoice billed to these elevators — the Invoices subtab. */
  servicedInvoices: number | undefined;
  /** Every other proposal for these elevators — the Proposals subtab. */
  servicedProposals: number | undefined;
  contracts: number | undefined;
}

/**
 * The numbers on the relational tabs' labels. They ride on the tab strip, so they are
 * fetched whether or not those tabs are open — but cheaply: single-row list requests
 * read only for their totals.
 */
export function useRelatedCounts(
  invoice: Invoice | undefined,
  tickets: Ticket[] | undefined,
): RelatedCounts {
  const carIds = useMemo(
    () => (invoice?.carsServiced ?? []).map((car) => car.id),
    [invoice?.carsServiced],
  );
  const countable = carIds.length > 0;

  const { numTickets: servicedTickets, isLoading: ticketsLoading } = useTickets(
    { cars: carIds, limit: 1 },
    countable,
  );

  // One read per class rather than one for both: the two now have a subtab each,
  // and a combined total can't be split back into them here.
  const { metadata: invoiceMetadata } = useInvoices(
    { cars: carIds, limit: '1', kind: [InvoiceKind.Invoice] },
    countable,
  );
  const { metadata: proposalMetadata } = useInvoices(
    { cars: carIds, limit: '1', kind: [InvoiceKind.Proposal] },
    countable,
  );

  const { contracts } = useContractsForProperty(invoice?.property?.id);
  const contractCount = useMemo(
    () => (invoice ? selectCoveringContracts(contracts, invoice).length : undefined),
    [contracts, invoice],
  );

  const parentTickets = useParentTickets(invoice, tickets);
  const linkedTickets =
    tickets === undefined || parentTickets === undefined
      ? undefined
      : tickets.length + parentTickets.length;

  const linkedInvoices = invoice?.relatedInvoices?.length ?? 0;

  /**
   * `ElevatorInvoicesTable` drops this invoice and anything already linked to it, so each
   * subtab's label has to drop them too. Counted per class: a linked proposal is missing
   * from the Invoices list's total, not from the Proposals one.
   *
   * Known skew: linking is offered across the whole property, so a linked invoice that
   * shares no serviced car was never in this `cars`-scoped total and subtracting it reads
   * one low. Erring low is the safer direction — the label never promises a row the table
   * won't show.
   */
  const servicedCount = (kind: InvoiceKindKey, total: number | undefined) => {
    if (!countable) return 0;
    if (total === undefined) return undefined;
    const self = invoice?.kind === kind ? 1 : 0;
    const linked = (invoice?.relatedInvoices ?? []).filter(
      (related) => related.kind === kind,
    ).length;
    return Math.max(total - self - linked, 0);
  };

  return {
    carsServiced: carIds.length,
    linkedTickets,
    // No serviced elevators means no such tickets, rather than an unknown count.
    servicedTickets: countable ? (ticketsLoading ? undefined : servicedTickets) : 0,
    linkedInvoices,
    servicedInvoices: servicedCount(InvoiceKind.Invoice, invoiceMetadata?.numInvoices),
    servicedProposals: servicedCount(InvoiceKind.Proposal, proposalMetadata?.numInvoices),
    contracts: contractCount,
  };
}
